// src/components/avatar/seasons/seasonMotifs.js
//
// The little picture "stamps" each season is built from — 4 per season.
// Every motif is drawn around (0,0), about 13 units across, in the same
// enamel-pin style as avatarWear.js PIN_ART (ink outline 0.9, flat fills,
// one shine). They are the season's pins as they are, and are scaled up or
// down for prints on clothes, glasses corners, frames, backgrounds, hats,
// pets' accessories and hair clips.
//
//   MOTIFS[season][name]  JSX
//   motif(season, name)   JSX or null
//   MOTIF_NAMES[season]   the 4 names in order
//   SMALL                 extra shared bits: snowflake, sparkle, star, holly, blossom, confetti

import React from "react";

export const INK = "#2e2430";
const W = { stroke: INK, strokeWidth: 0.9, strokeLinejoin: "round", strokeLinecap: "round" };

export const starPath = (cx, cy, r, inner = 0.48, n = 5) => {
  let s = "";
  for (let i = 0; i < n * 2; i += 1) {
    const a = (Math.PI / n) * i - Math.PI / 2;
    const rr = i % 2 ? r * inner : r;
    s += `${i ? "L" : "M"}${(cx + Math.cos(a) * rr).toFixed(2)} ${(cy + Math.sin(a) * rr).toFixed(2)} `;
  }
  return `${s}Z`;
};

export const heartPath = (cx, cy, s = 1) =>
  `M${cx} ${cy + 5.4 * s} C${cx - 6 * s} ${cy + 1.6 * s} ${cx - 7 * s} ${cy - 2 * s} ${cx - 5.6 * s} ${cy - 4 * s} C${cx - 4 * s} ${cy - 6 * s} ${cx - 1.4 * s} ${cy - 5.4 * s} ${cx} ${cy - 3 * s} C${cx + 1.4 * s} ${cy - 5.4 * s} ${cx + 4 * s} ${cy - 6 * s} ${cx + 5.6 * s} ${cy - 4 * s} C${cx + 7 * s} ${cy - 2 * s} ${cx + 6 * s} ${cy + 1.6 * s} ${cx} ${cy + 5.4 * s} Z`;

const Shine = ({ x, y, rx = 0.9, ry = 1.5, r = -20 }) => <ellipse cx={x} cy={y} rx={rx} ry={ry} fill="#fff" opacity="0.65" transform={`rotate(${r} ${x} ${y})`} />;

const Burst = ({ cols, r0 = 1.8, r1 = 6.4, n = 12 }) => (
  <g strokeLinecap="round">
    {Array.from({ length: n }, (_, i) => {
      const a = (i / n) * Math.PI * 2;
      const c = cols[i % cols.length];
      return (
        <g key={i}>
          <path d={`M${(Math.cos(a) * r0).toFixed(2)} ${(Math.sin(a) * r0).toFixed(2)} L${(Math.cos(a) * r1).toFixed(2)} ${(Math.sin(a) * r1).toFixed(2)}`} stroke={c} strokeWidth="1.3" />
          <circle cx={(Math.cos(a) * (r1 + 0.9)).toFixed(2)} cy={(Math.sin(a) * (r1 + 0.9)).toFixed(2)} r="0.55" fill={c} />
        </g>
      );
    })}
    <circle r="1.5" fill="#fff" stroke={INK} strokeWidth="0.5" />
  </g>
);

const Flame = ({ x = 0, y = 0, s = 1 }) => (
  <g transform={`translate(${x} ${y}) scale(${s})`}>
    <path d="M0 -3 C1.6 -1.2 1.6 0.6 0 1.2 C-1.6 0.6 -1.6 -1.2 0 -3 Z" fill="#ffb627" stroke="#e8590c" strokeWidth="0.45" />
    <path d="M0 -1.6 C0.7 -0.6 0.7 0.4 0 0.8 C-0.7 0.4 -0.7 -0.6 0 -1.6 Z" fill="#fff4b0" />
  </g>
);

// ---------------------------------------------------------------------------
// shared bits
// ---------------------------------------------------------------------------
export const SMALL = {
  snowflake: (
    <g stroke="#5aa2ff" strokeWidth="1.1" strokeLinecap="round" fill="none">
      {[0, 60, 120].map((a) => (
        <path key={a} transform={`rotate(${a})`} d="M0 -5.6 L0 5.6 M0 -3.6 L-1.6 -5 M0 -3.6 L1.6 -5 M0 3.6 L-1.6 5 M0 3.6 L1.6 5" />
      ))}
    </g>
  ),
  sparkle: <path d="M0 -5 L1.3 -1.3 L5 0 L1.3 1.3 L0 5 L-1.3 1.3 L-5 0 L-1.3 -1.3 Z" fill="#fff6b8" stroke="#e0b100" strokeWidth="0.5" strokeLinejoin="round" />,
  star: <path d={starPath(0, 0.4, 6)} fill="#ffd23f" {...W} />,
  holly: (
    <g {...W}>
      <path d="M0 0 C-2 -4 -6 -4 -7 -3 C-6 -2 -6.4 -0.6 -5.4 0.2 C-6.4 1.2 -5.6 2.6 -3.6 2.4 C-2.6 3.6 -1 2.4 0 0 Z" fill="#2fae5b" />
      <path d="M0 0 C2 -4 6 -4 7 -3 C6 -2 6.4 -0.6 5.4 0.2 C6.4 1.2 5.6 2.6 3.6 2.4 C2.6 3.6 1 2.4 0 0 Z" fill="#2fae5b" />
      <circle cx="-1.3" cy="1" r="1.7" fill="#e23b3b" />
      <circle cx="1.3" cy="1.2" r="1.7" fill="#e23b3b" />
      <circle cx="0" cy="-0.9" r="1.7" fill="#e23b3b" />
    </g>
  ),
  blossom: (
    <g {...W} strokeWidth="0.7">
      {[0, 72, 144, 216, 288].map((a) => (
        <circle key={a} cx="0" cy="-2.6" r="2.3" fill="#ffc2d9" transform={`rotate(${a})`} />
      ))}
      <circle r="1.5" fill="#f6c343" />
    </g>
  ),
  confetti: (
    <g>
      <rect x="-4" y="-3" width="2.2" height="1.2" fill="#ff5fa2" transform="rotate(30 -3 -2.4)" />
      <rect x="1.6" y="-4.4" width="2.2" height="1.2" fill="#4fd1ff" transform="rotate(-25 2.7 -3.8)" />
      <rect x="-1" y="2" width="2.2" height="1.2" fill="#ffd23f" transform="rotate(60 0 2.6)" />
      <circle cx="3.6" cy="2.4" r="0.8" fill="#7ed957" />
      <circle cx="-4" cy="2.6" r="0.7" fill="#b07cff" />
    </g>
  ),
  heart: <path d={heartPath(0, 0)} fill="#ff4d6d" {...W} />,
};

// ---------------------------------------------------------------------------
// the motifs
// ---------------------------------------------------------------------------
export const MOTIFS = {
  newyear: {
    firework: <Burst cols={["#ffd23f", "#ff5fa2", "#4fd1ff", "#7ed957"]} />,
    horn: (
      <g {...W}>
        <path d="M-6.4 -0.8 L2.6 -3.6 L2.6 3.6 L-6.4 0.8 Z" fill="#ff5fa2" />
        <path d="M-3.6 -1.6 L-3.6 1.6 M-0.6 -2.6 L-0.6 2.6" stroke="#ffd23f" strokeWidth="1.2" />
        <ellipse cx="3.6" cy="0" rx="2" ry="4.4" fill="#ffd23f" />
        <ellipse cx="4" cy="0" rx="0.8" ry="2.4" fill="#e0a800" stroke="none" />
        <path d="M-6.4 0 q-0.6 -2.6 1 -3.4" fill="none" stroke="#4fd1ff" strokeWidth="0.8" />
      </g>
    ),
    clock: (
      <g {...W}>
        <circle r="6" fill="#f2c230" />
        <circle r="4.6" fill="#fffaf0" />
        {[0, 90, 180, 270].map((a) => (
          <path key={a} d="M0 -3.8 L0 -3" transform={`rotate(${a})`} strokeWidth="0.7" />
        ))}
        <path d="M0 0 L0 -3.2 M0 0 L0.3 -2.2" strokeWidth="1" />
        <circle r="0.6" fill={INK} />
        <path d="M5.4 -5.4 l0.6 -1.6 0.6 1.6" stroke="#fff" strokeWidth="0.6" fill="none" />
      </g>
    ),
    discoball: (
      <g {...W}>
        <path d="M0 -7 L0 -5.6" strokeWidth="0.7" />
        <circle r="5.6" fill="#c9d3e6" />
        <path d="M-5.6 0 H5.6 M-4.8 -2.8 H4.8 M-4.8 2.8 H4.8 M0 -5.6 V5.6 M-3 -4.8 Q-4.2 0 -3 4.8 M3 -4.8 Q4.2 0 3 4.8" stroke="#8d97ad" strokeWidth="0.5" fill="none" />
        <rect x="-2.8" y="-2.6" width="2.6" height="2.4" fill="#ff8cc6" stroke="none" opacity="0.8" />
        <rect x="0.4" y="0.4" width="2.4" height="2.2" fill="#7fe0ff" stroke="none" opacity="0.8" />
        <path d="M-2.8 -4 l0.6 -1.4 0.6 1.4 -0.6 1.4 Z" fill="#fff" stroke="none" />
      </g>
    ),
  },
  lunarnewyear: {
    lantern: (
      <g {...W}>
        <path d="M0 -7 L0 -5.6" strokeWidth="0.7" />
        <rect x="-2.6" y="-5.8" width="5.2" height="1.4" rx="0.5" fill="#f6c343" />
        <ellipse rx="5.8" ry="4.6" fill="#e03131" />
        <path d="M0 -4.6 V4.6 M-2.6 -4.2 Q-4.4 0 -2.6 4.2 M2.6 -4.2 Q4.4 0 2.6 4.2" stroke="#a51111" strokeWidth="0.6" fill="none" />
        <rect x="-2.6" y="4.4" width="5.2" height="1.4" rx="0.5" fill="#f6c343" />
        <path d="M0 5.8 V7.6 M-0.8 7 L0 8.4 L0.8 7" stroke="#f6c343" strokeWidth="0.9" fill="none" />
        <Shine x={-3} y={-1.4} />
      </g>
    ),
    envelope: (
      <g {...W}>
        <rect x="-4.6" y="-6" width="9.2" height="12" rx="1" fill="#e03131" />
        <path d="M-4.6 -3.4 Q0 0 4.6 -3.4" fill="none" stroke="#f6c343" strokeWidth="0.8" />
        <circle cy="1.4" r="2.2" fill="#f6c343" />
        <circle cy="1.4" r="1" fill="#e03131" stroke="none" />
        <path d="M-3 4.6 H3" stroke="#f6c343" strokeWidth="0.7" />
      </g>
    ),
    mandarin: (
      <g {...W}>
        <circle cy="0.8" r="5.4" fill="#ff8c1a" />
        <path d="M0 -4.6 Q2.6 -7.2 5 -5.4 Q2.4 -3.6 0 -4.6 Z" fill="#2fae5b" />
        <path d="M0 -4.6 Q-2.4 -6.6 -4.4 -5 Q-2.2 -3.6 0 -4.6 Z" fill="#2fae5b" />
        <circle cx="-2" cy="-1" r="0.5" fill="#ffd08a" stroke="none" />
        <circle cx="1.6" cy="2.4" r="0.5" fill="#ffd08a" stroke="none" />
        <Shine x={-2.6} y={0.6} />
      </g>
    ),
    firecracker: (
      <g {...W} transform="rotate(-25)">
        <rect x="-2.6" y="-4.6" width="5.2" height="10" rx="1" fill="#e03131" />
        <path d="M-2.6 -2.6 H2.6 M-2.6 3.4 H2.6" stroke="#f6c343" strokeWidth="1.1" />
        <path d="M0 -4.6 Q1.4 -6.4 0.4 -7.4" fill="none" stroke="#6b4a2b" strokeWidth="0.7" />
        <g transform="translate(0.4 -7.6) scale(0.5)">{SMALL.sparkle}</g>
      </g>
    ),
  },
  valentines: {
    heart: (
      <g>
        <path d={heartPath(0, 0.2, 1.1)} fill="#ff4d6d" {...W} />
        <Shine x={-3.2} y={-2} />
      </g>
    ),
    letter: (
      <g {...W}>
        <rect x="-6" y="-4" width="12" height="8.4" rx="1" fill="#fff8fb" />
        <path d="M-6 -4 L0 1 L6 -4" fill="none" stroke="#e8a0b6" />
        <path d={heartPath(0, 1, 0.42)} fill="#e83f6f" strokeWidth="0.6" />
      </g>
    ),
    rose: (
      <g {...W}>
        <path d="M0 1 Q-0.6 4 0.4 7" fill="none" stroke="#2f8a3e" strokeWidth="1" />
        <path d="M0.2 4.6 Q3 3 4 4.4 Q2 6 0.2 4.6 Z" fill="#2fae5b" />
        <circle cy="-1.6" r="4.6" fill="#e5383b" />
        <path d="M-2 -1.8 Q0 -4.6 2 -1.8 Q0 0.6 -2 -1.8 Z M-3.6 -0.6 Q-2 2 0 1.6 M3.6 -0.6 Q2 2 0 1.6" fill="none" stroke="#a4161a" strokeWidth="0.7" />
      </g>
    ),
    lollipop: (
      <g {...W}>
        <path d="M0 2.4 L0 7.4" stroke="#fff" strokeWidth="1.4" />
        <path d="M0 2.4 L0 7.4" fill="none" strokeWidth="0.4" />
        <path d={heartPath(0, -1.6, 0.95)} fill="#ff8cc6" />
        <path d="M-2.6 -3 Q0 -0.8 2.6 -3 M-2.6 -0.6 Q0 1.4 2.6 -0.6" stroke="#fff" strokeWidth="0.8" fill="none" />
      </g>
    ),
  },
  stpatricks: {
    shamrock: (
      <g {...W}>
        <path d="M0 1 Q1 4.6 3 6.6" fill="none" stroke="#1f7a3a" strokeWidth="1.1" />
        {[0, 120, 240].map((a) => (
          <path key={a} d={heartPath(0, -3, 0.62)} fill="#2fbf5b" transform={`rotate(${a} 0 0.2)`} />
        ))}
        <circle cy="0.2" r="0.8" fill="#1f7a3a" stroke="none" />
      </g>
    ),
    potofgold: (
      <g {...W}>
        <ellipse cy="-2.6" rx="5" ry="2" fill="#ffd23f" />
        <circle cx="-2.4" cy="-3.4" r="1.4" fill="#ffe27a" />
        <circle cx="1.6" cy="-3.8" r="1.4" fill="#ffe27a" />
        <path d="M-5.6 -2 Q-6 5 0 5.6 Q6 5 5.6 -2 Z" fill="#2b2b33" />
        <path d="M-5.8 -1.6 H5.8" stroke="#55555f" strokeWidth="1.4" />
        <path d="M-3.6 4.6 L-4 6.4 M3.6 4.6 L4 6.4" strokeWidth="1" />
      </g>
    ),
    coin: (
      <g {...W}>
        <circle r="5.6" fill="#f2c230" />
        <circle r="4.2" fill="none" stroke="#d29a00" strokeWidth="0.7" />
        <g transform="scale(0.5)">
          {[0, 120, 240].map((a) => (
            <path key={a} d={heartPath(0, -3, 0.62)} fill="#ffe27a" stroke="#d29a00" strokeWidth="1" transform={`rotate(${a} 0 0.2)`} />
          ))}
        </g>
        <Shine x={-3} y={-2.4} />
      </g>
    ),
    horseshoe: (
      <g>
        <path d="M-4 6 L-4.6 0 A4.6 4.6 0 0 1 4.6 0 L4 6" fill="none" stroke={INK} strokeWidth="3.6" strokeLinecap="round" />
        <path d="M-4 6 L-4.6 0 A4.6 4.6 0 0 1 4.6 0 L4 6" fill="none" stroke="#c0c7d4" strokeWidth="2.2" strokeLinecap="round" />
        {[[-4.6, 3], [-4.1, -2.6], [4.6, 3], [4.1, -2.6], [0, -4.6]].map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="0.45" fill={INK} />
        ))}
      </g>
    ),
  },
  easter: {
    egg: (
      <g {...W}>
        <path d="M0 -6.4 C4 -6.4 5.4 0 5.2 2 C5 5 2.8 6.4 0 6.4 C-2.8 6.4 -5 5 -5.2 2 C-5.4 0 -4 -6.4 0 -6.4 Z" fill="#b69cf2" />
        <path d="M-5.2 0.4 L-3.4 -1.4 L-1.7 0.4 L0 -1.4 L1.7 0.4 L3.4 -1.4 L5.2 0.4" fill="none" stroke="#fff" strokeWidth="1.1" />
        <circle cx="-2" cy="3.4" r="0.9" fill="#ffe27a" stroke="none" />
        <circle cx="1.8" cy="3.6" r="0.9" fill="#7fd8be" stroke="none" />
        <circle cx="0" cy="-3.6" r="0.9" fill="#ff9ec4" stroke="none" />
      </g>
    ),
    chick: (
      <g {...W}>
        <circle cy="-0.6" r="4.6" fill="#ffe14d" />
        <path d="M-2 -6 q1 -1.6 2 0 q1 -1.6 2 0" fill="none" strokeWidth="0.6" />
        <circle cx="-1.6" cy="-1.4" r="0.6" fill={INK} stroke="none" />
        <circle cx="1.6" cy="-1.4" r="0.6" fill={INK} stroke="none" />
        <path d="M-0.9 0 L0.9 0 L0 1.2 Z" fill="#ff9a3c" strokeWidth="0.5" />
        <path d="M-5.4 1.6 L-3.6 3.4 L-1.8 1.8 L0 3.4 L1.8 1.8 L3.6 3.4 L5.4 1.6 L5 6 Q0 7.6 -5 6 Z" fill="#fff" />
      </g>
    ),
    bunny: (
      <g {...W}>
        <ellipse cx="-2.2" cy="-4" rx="1.6" ry="3.6" fill="#fff" transform="rotate(-10 -2.2 -4)" />
        <ellipse cx="2.2" cy="-4" rx="1.6" ry="3.6" fill="#fff" transform="rotate(10 2.2 -4)" />
        <ellipse cx="-2.2" cy="-4" rx="0.7" ry="2.4" fill="#ffb3c8" stroke="none" transform="rotate(-10 -2.2 -4)" />
        <ellipse cx="2.2" cy="-4" rx="0.7" ry="2.4" fill="#ffb3c8" stroke="none" transform="rotate(10 2.2 -4)" />
        <circle cy="2" r="4.4" fill="#fff" />
        <circle cx="-1.6" cy="1.4" r="0.55" fill={INK} stroke="none" />
        <circle cx="1.6" cy="1.4" r="0.55" fill={INK} stroke="none" />
        <path d="M-0.6 2.6 h1.2 l-0.6 0.7 Z" fill="#ff8cb0" strokeWidth="0.4" />
        <ellipse cx="-2.8" cy="3.4" rx="0.9" ry="0.5" fill="#ffb3c8" stroke="none" />
        <ellipse cx="2.8" cy="3.4" rx="0.9" ry="0.5" fill="#ffb3c8" stroke="none" />
      </g>
    ),
    tulip: (
      <g {...W}>
        <path d="M0 1 L0 7" fill="none" stroke="#2f8a3e" strokeWidth="1" />
        <path d="M0 5 Q-3.6 3 -4 5.4 Q-2 6.6 0 5.6 Z" fill="#4cc35b" strokeWidth="0.6" />
        <path d="M-4 -4.6 L-2 -2.4 L0 -5.6 L2 -2.4 L4 -4.6 Q4.6 1.4 0 2 Q-4.6 1.4 -4 -4.6 Z" fill="#ff7eb6" />
      </g>
    ),
  },
  july4: {
    star: (
      <g>
        <path d={starPath(0, 0.4, 6.4)} fill="#1f3f8f" {...W} />
        <path d={starPath(0, 0.6, 3.4)} fill="#fff" stroke="#e23b4a" strokeWidth="0.6" strokeLinejoin="round" />
      </g>
    ),
    flag: (
      <g {...W}>
        <path d="M-6 -5 L-6 6.6" strokeWidth="1" />
        <rect x="-5.6" y="-5" width="11.6" height="7.6" fill="#fff" />
        {[0, 2, 4, 6].map((k) => (
          <rect key={k} x="-5.6" y={-5 + k * 0.95 + 0.0} width="11.6" height="0.95" fill="#e23b4a" stroke="none" />
        ))}
        <rect x="-5.6" y="-5" width="5" height="4" fill="#1f3f8f" strokeWidth="0.5" />
        {[[-4.4, -4], [-2.4, -4], [-3.4, -2.8], [-4.4, -1.6], [-2.4, -1.6]].map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="0.4" fill="#fff" stroke="none" />
        ))}
        <rect x="-5.6" y="-5" width="11.6" height="7.6" fill="none" />
      </g>
    ),
    firework: <Burst cols={["#e23b4a", "#ffffff", "#3a6fe0"]} n={14} />,
    popsicle: (
      <g {...W}>
        <rect x="-0.8" y="3" width="1.6" height="4" rx="0.6" fill="#e8c08a" />
        <path d="M-3.4 3.6 L-3.4 -1.4 Q-3.4 -6.6 0 -6.6 Q3.4 -6.6 3.4 -1.4 L3.4 3.6 Z" fill="#fff" />
        <path d="M-3.4 -1.4 Q-3.4 -6.6 0 -6.6 Q3.4 -6.6 3.4 -1.4 Z" fill="#e23b4a" />
        <rect x="-3.4" y="1" width="6.8" height="2.6" fill="#3a6fe0" />
      </g>
    ),
  },
  halloween: {
    pumpkin: (
      <g {...W}>
        <path d="M0 -4.6 Q0.4 -6.4 2 -6.8" fill="none" stroke="#3f7a2a" strokeWidth="1.3" />
        <ellipse cx="-2.6" cy="0.6" rx="3.6" ry="4.8" fill="#ff8a1f" />
        <ellipse cx="2.6" cy="0.6" rx="3.6" ry="4.8" fill="#ff8a1f" />
        <ellipse cy="0.6" rx="2.6" ry="5" fill="#ff9b3a" />
        <path d="M-3 -0.6 l1.2 -1.6 1.2 1.6 Z M1 -0.6 l1.2 -1.6 1.2 1.6 Z" fill={INK} strokeWidth="0.3" />
        <path d="M-3 1.8 Q0 4.4 3 1.8 Q0 3 -3 1.8 Z" fill={INK} strokeWidth="0.4" />
      </g>
    ),
    ghost: (
      <g {...W}>
        <path d="M-5 6 L-5 -0.6 Q-5 -6.4 0 -6.4 Q5 -6.4 5 -0.6 L5 6 L3.4 4.6 L1.7 6 L0 4.6 L-1.7 6 L-3.4 4.6 Z" fill="#fff" />
        <ellipse cx="-1.8" cy="-1.6" rx="0.8" ry="1.1" fill={INK} stroke="none" />
        <ellipse cx="1.8" cy="-1.6" rx="0.8" ry="1.1" fill={INK} stroke="none" />
        <ellipse cy="1" rx="0.9" ry="0.6" fill={INK} stroke="none" />
        <ellipse cx="-3" cy="0.4" rx="0.8" ry="0.45" fill="#ffb3c8" stroke="none" />
        <ellipse cx="3" cy="0.4" rx="0.8" ry="0.45" fill="#ffb3c8" stroke="none" />
      </g>
    ),
    bat: (
      <g {...W}>
        <path d="M-1.6 0 Q-4 -3.6 -7 -2 Q-6 -0.6 -6.4 1 Q-5 0.4 -4.4 1.8 Q-3.2 0.6 -1.6 2 Z" fill="#5b3a8c" />
        <path d="M1.6 0 Q4 -3.6 7 -2 Q6 -0.6 6.4 1 Q5 0.4 4.4 1.8 Q3.2 0.6 1.6 2 Z" fill="#5b3a8c" />
        <circle cy="0.4" r="2.8" fill="#6b4aa0" />
        <path d="M-2 -1.6 L-1.8 -3.6 L-0.8 -2.2 M2 -1.6 L1.8 -3.6 L0.8 -2.2" fill="#6b4aa0" />
        <circle cx="-1" cy="0" r="0.6" fill="#ffe14d" stroke="none" />
        <circle cx="1" cy="0" r="0.6" fill="#ffe14d" stroke="none" />
        <path d="M-0.6 1.6 L-0.3 2.3 L0 1.6 L0.3 2.3 L0.6 1.6" fill="#fff" strokeWidth="0.3" />
      </g>
    ),
    candy: (
      <g {...W}>
        <path d="M0 6.4 L-5 -4.6 Q0 -6.6 5 -4.6 Z" fill="#fff" />
        <path d="M-2.7 0.4 L-5 -4.6 Q0 -6.6 5 -4.6 L2.7 0.4 Q0 -0.4 -2.7 0.4 Z" fill="#ffd23f" />
        <path d="M-1.4 3.4 L-2.7 0.4 Q0 -0.4 2.7 0.4 L1.4 3.4 Q0 3 -1.4 3.4 Z" fill="#ff8a1f" />
      </g>
    ),
  },
  diwali: {
    diya: (
      <g {...W}>
        <Flame y={-2.6} s={1.3} />
        <path d="M-6.4 0 Q0 -1.2 6.4 0 Q5 5.4 0 5.6 Q-5 5.4 -6.4 0 Z" fill="#d9662b" />
        <path d="M-5 1.4 Q0 3 5 1.4" fill="none" stroke="#f6c343" strokeWidth="0.8" />
        {[-3.4, 0, 3.4].map((x) => (
          <circle key={x} cx={x} cy="3.4" r="0.6" fill="#f6c343" stroke="none" />
        ))}
      </g>
    ),
    rangoli: (
      <g>
        {Array.from({ length: 8 }, (_, i) => (
          <ellipse key={`a${i}`} cx="0" cy="-4.4" rx="1.6" ry="2.4" fill={["#d6246e", "#ff9f1c"][i % 2]} stroke={INK} strokeWidth="0.5" transform={`rotate(${i * 45})`} />
        ))}
        {Array.from({ length: 8 }, (_, i) => (
          <circle key={`b${i}`} cx="0" cy="-6.6" r="0.6" fill="#2ec4b6" transform={`rotate(${i * 45 + 22.5})`} />
        ))}
        <circle r="2.4" fill="#f6c343" stroke={INK} strokeWidth="0.6" />
        <circle r="1" fill="#2ec4b6" />
      </g>
    ),
    marigold: (
      <g stroke={INK} strokeWidth="0.5">
        {Array.from({ length: 12 }, (_, i) => (
          <circle key={`o${i}`} cx="0" cy="-4.4" r="2" fill="#ff8c1a" transform={`rotate(${i * 30})`} />
        ))}
        {Array.from({ length: 8 }, (_, i) => (
          <circle key={`i${i}`} cx="0" cy="-2.4" r="1.7" fill="#ffb627" transform={`rotate(${i * 45 + 20})`} />
        ))}
        <circle r="1.4" fill="#ffd23f" />
      </g>
    ),
    sparkler: (
      <g>
        <path d="M-4.4 6.4 L1.4 -1.4" stroke="#8d97ad" strokeWidth="1.1" strokeLinecap="round" />
        <path d="M-4.4 6.4 L-2.6 4" stroke="#6b4a2b" strokeWidth="1.4" strokeLinecap="round" />
        <g transform="translate(2.2 -2.4)">
          {Array.from({ length: 10 }, (_, i) => {
            const a = (i / 10) * Math.PI * 2;
            return <path key={i} d={`M0 0 L${(Math.cos(a) * 4.4).toFixed(2)} ${(Math.sin(a) * 4.4).toFixed(2)}`} stroke={i % 2 ? "#ffd23f" : "#ff9f1c"} strokeWidth="0.8" strokeLinecap="round" />;
          })}
          <circle r="1.3" fill="#fff6b8" />
        </g>
      </g>
    ),
  },
  thanksgiving: {
    turkey: (
      <g {...W} strokeWidth="0.7">
        {["#e5383b", "#ff8a1f", "#f2c230", "#7fb03a", "#f2c230", "#ff8a1f", "#e5383b"].map((c, i) => (
          <ellipse key={i} cx="0" cy="-4.2" rx="1.7" ry="3.2" fill={c} transform={`rotate(${-72 + i * 24} 0 1)`} />
        ))}
        <circle cy="1.6" r="3.8" fill="#8a4b22" />
        <circle cy="-1.2" r="2.2" fill="#a0602e" />
        <circle cx="-0.8" cy="-1.6" r="0.4" fill={INK} stroke="none" />
        <circle cx="0.8" cy="-1.6" r="0.4" fill={INK} stroke="none" />
        <path d="M-0.6 -0.8 L0.6 -0.8 L0 0 Z" fill="#f2c230" strokeWidth="0.3" />
        <path d="M0.3 -0.2 q0.6 1.2 -0.2 1.6" fill="none" stroke="#e5383b" strokeWidth="0.8" />
      </g>
    ),
    pie: (
      <g {...W}>
        <ellipse cy="1.6" rx="6.4" ry="3.4" fill="#d9a066" />
        <ellipse cy="0.6" rx="5.4" ry="2.6" fill="#e07b2a" />
        <path d="M-6.4 1.6 Q-6.6 4 -5 4.6 Q0 6.6 5 4.6 Q6.6 4 6.4 1.6" fill="#c98a4b" />
        <circle cx="0.6" cy="-0.6" r="1.8" fill="#fffaf0" />
        <circle cx="1.6" cy="-1.2" r="1.2" fill="#fffaf0" />
      </g>
    ),
    leaf: (
      <g {...W}>
        <path d="M0 6.6 L0 2.6" strokeWidth="1" />
        <path d="M0 2.6 L-1.4 3.4 L-1.6 2 L-5 2.6 L-4 0.6 L-6.4 -0.6 L-4.4 -1.6 L-5 -4 L-2.6 -3.2 L-1.6 -6 L0 -4.2 L1.6 -6 L2.6 -3.2 L5 -4 L4.4 -1.6 L6.4 -0.6 L4 0.6 L5 2.6 L1.6 2 L1.4 3.4 Z" fill="#e8601c" />
        <path d="M0 2.6 L0 -4 M0 0 L-3.4 -1.6 M0 0 L3.4 -1.6" stroke="#a8380c" strokeWidth="0.6" fill="none" />
      </g>
    ),
    acorn: (
      <g {...W}>
        <path d="M-3.8 -0.6 Q-4 5.6 0 6.4 Q4 5.6 3.8 -0.6 Z" fill="#c98a4b" />
        <path d="M-5 -0.6 Q-5 -4.6 0 -4.8 Q5 -4.6 5 -0.6 Q0 0.8 -5 -0.6 Z" fill="#7a4a24" />
        <path d="M-3 -2.6 l1.2 0.8 M0 -3.4 l0 1.2 M3 -2.6 l-1.2 0.8" stroke="#a06a3a" strokeWidth="0.6" />
        <path d="M0 -4.8 Q0.6 -6.4 1.8 -6.6" fill="none" strokeWidth="1" />
        <Shine x={-1.8} y={2} />
      </g>
    ),
  },
  hanukkah: {
    dreidel: (
      <g {...W}>
        <rect x="-0.9" y="-7" width="1.8" height="2.6" rx="0.4" fill="#c9d3e6" />
        <path d="M-4.6 -4.6 H4.6 V1.6 L0 6.6 L-4.6 1.6 Z" fill="#2a5bd7" />
        <path d="M-4.6 -4.6 H0.4 V3.4 L-4.6 1.6 Z" fill="#4f7ff0" stroke="none" />
        <path d="M-4.6 -4.6 H4.6 V1.6 L0 6.6 L-4.6 1.6 Z" fill="none" />
        <path d="M-2.4 -2 Q0 -2.8 2.4 -2 M-2.4 0.2 Q0 -0.6 2.4 0.2" stroke="#fff" strokeWidth="0.8" fill="none" />
      </g>
    ),
    menorah: (
      <g {...W} strokeWidth="0.6">
        <path d="M-3 6.4 H3 M0 6.4 V2.4" strokeWidth="1.2" stroke="#d29a00" />
        <path d="M-6 -1.6 Q-6 2.4 0 2.4 Q6 2.4 6 -1.6" fill="none" stroke="#d29a00" strokeWidth="1" />
        {[-6, -4.5, -3, -1.5, 0, 1.5, 3, 4.5, 6].map((x) => (
          <g key={x}>
            <rect x={x - 0.45} y={x === 0 ? -4.6 : -3.4} width="0.9" height={x === 0 ? 3 : 1.8} fill="#e6efff" />
            <Flame x={x} y={x === 0 ? -5.4 : -4.2} s={0.4} />
          </g>
        ))}
      </g>
    ),
    gelt: (
      <g {...W}>
        <circle cx="1.6" cy="1.4" r="4.6" fill="#d9a400" />
        <circle cx="-1" cy="-0.8" r="4.8" fill="#f2c230" />
        <circle cx="-1" cy="-0.8" r="3.6" fill="none" stroke="#d29a00" strokeWidth="0.6" />
        <g transform="translate(-1 -0.8) scale(0.38)">
          <path d="M-4.6 -4.6 H4.6 V1.6 L0 6.6 L-4.6 1.6 Z" fill="#ffe27a" stroke="#c48a00" strokeWidth="1.2" />
        </g>
        <Shine x={-3.4} y={-2.6} />
      </g>
    ),
    donut: (
      <g {...W}>
        <ellipse cy="1" rx="6" ry="4.6" fill="#e0a24e" />
        <ellipse cy="-0.2" rx="5.2" ry="3.6" fill="#f6d8a6" stroke="none" />
        {[[-3, -1], [-1.4, 0.6], [1.6, -1.6], [3.2, 0.2], [0, -2], [-3.6, 1.4], [2.4, 1.6]].map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="0.5" fill="#fff" stroke="none" />
        ))}
        <ellipse cx="4.4" cy="1.6" rx="1.4" ry="1" fill="#d61f45" strokeWidth="0.5" />
      </g>
    ),
  },
  christmas: {
    tree: (
      <g {...W}>
        <rect x="-1.2" y="4.4" width="2.4" height="2.4" fill="#8a5a2b" />
        <path d="M0 -5.4 L-5.6 4.6 L5.6 4.6 Z" fill="#2fae5b" />
        <path d="M-3.4 0.6 Q0 2 3.4 0.6" fill="none" stroke="#f2c230" strokeWidth="0.7" />
        <circle cx="-2.4" cy="3" r="0.75" fill="#e23b3b" stroke="none" />
        <circle cx="1.6" cy="-0.6" r="0.75" fill="#3a86ff" stroke="none" />
        <circle cx="2.8" cy="3.2" r="0.75" fill="#ffd23f" stroke="none" />
        <path d={starPath(0, -5.6, 2.2)} fill="#ffd23f" strokeWidth="0.6" />
      </g>
    ),
    ornament: (
      <g {...W}>
        <path d="M-1 -6.6 Q0 -8 1 -6.6" fill="none" strokeWidth="0.6" />
        <rect x="-1.8" y="-6.4" width="3.6" height="1.8" rx="0.4" fill="#f2c230" />
        <circle cy="0.6" r="5.4" fill="#e23b3b" />
        <path d="M-5.2 -0.4 Q0 1.8 5.2 -0.4 M-4.6 2.6 Q0 4.6 4.6 2.6" fill="none" stroke="#fff" strokeWidth="0.9" />
        <Shine x={-2.6} y={-1.4} />
      </g>
    ),
    stocking: (
      <g {...W}>
        <path d="M-2.6 -3.4 L-2.6 2 Q-2.6 3 -3.6 3.6 Q-5.4 4.8 -4.6 6.2 Q-3.6 7.4 -1.2 6.2 L3 4 Q3.8 3.4 3.8 2.4 L3.8 -3.4 Z" fill="#e23b3b" />
        <rect x="-3.6" y="-6.2" width="8.4" height="3.2" rx="1.4" fill="#fff" />
        <path d="M-4.6 6.2 Q-3.6 7.4 -1.2 6.2 L-1.6 4.6 Q-3.6 4.6 -4.6 6.2 Z" fill="#2fae5b" />
      </g>
    ),
    bell: (
      <g {...W}>
        <path d="M-5 3.6 Q-4.6 -1 -3.2 -2.6 Q0 -5.6 3.2 -2.6 Q4.6 -1 5 3.6 Z" fill="#f2c230" />
        <circle cy="4.6" r="1.4" fill="#d29a00" />
        <path d="M-2 -5 Q-4.6 -7.6 -5.4 -5.4 Q-4.4 -4 -2 -5 Z M2 -5 Q4.6 -7.6 5.4 -5.4 Q4.4 -4 2 -5 Z" fill="#e23b3b" />
        <circle cy="-5" r="1.1" fill="#e23b3b" />
        <Shine x={-2.4} y={-0.6} />
      </g>
    ),
  },
  kwanzaa: {
    kinara: (
      <g {...W} strokeWidth="0.6">
        <rect x="-6.4" y="2.4" width="12.8" height="2.2" rx="0.6" fill="#8a5a2b" />
        <path d="M-2.4 4.6 L-3.4 6.6 H3.4 L2.4 4.6" fill="#6b4422" />
        {[-5.4, -3.6, -1.8, 0, 1.8, 3.6, 5.4].map((x, i) => (
          <g key={x}>
            <rect x={x - 0.6} y={i === 3 ? -3.6 : -2.4} width="1.2" height={i === 3 ? 6 : 4.8} fill={i < 3 ? "#c62f2f" : i === 3 ? "#2a2420" : "#1f8a3e"} />
            <Flame x={x} y={i === 3 ? -4.4 : -3.2} s={0.45} />
          </g>
        ))}
      </g>
    ),
    cup: (
      <g {...W}>
        <path d="M-5 -5.4 H5 Q5 1 0 1.6 Q-5 1 -5 -5.4 Z" fill="#f2c230" />
        <path d="M0 1.6 V4.6" strokeWidth="1.4" stroke="#d29a00" />
        <ellipse cy="5.4" rx="3.8" ry="1.3" fill="#f2c230" />
        <path d="M-3.4 -3 H3.4" stroke="#c62f2f" strokeWidth="0.9" />
        <path d="M-3 -1.4 H3" stroke="#1f8a3e" strokeWidth="0.9" />
        <Shine x={-2.6} y={-2.6} />
      </g>
    ),
    corn: (
      <g {...W}>
        <path d="M0 -6.6 C3 -6 3.4 2 0.6 5.4 C-2.4 2 -3 -6 0 -6.6 Z" fill="#f6c343" />
        {[[-1, -4], [1, -4], [-1.4, -1.6], [0.6, -1.6], [-1, 0.8], [1, 0.8], [0, 2.8]].map(([x, y], i) => (
          <circle key={i} cx={x + 0.2} cy={y} r="0.7" fill="#ffe27a" strokeWidth="0.3" />
        ))}
        <path d="M0.6 5.4 Q-5 3 -4.6 -3 Q-2.4 1 0.4 2.4 Z" fill="#7fb03a" />
        <path d="M0.6 5.4 Q5.6 3.4 5 -2.4 Q3 1.4 0.8 2.4 Z" fill="#4c9a2a" />
      </g>
    ),
    basket: (
      <g {...W}>
        <circle cx="-2.4" cy="-1.6" r="2.4" fill="#e5383b" />
        <circle cx="2.2" cy="-2" r="2.2" fill="#ff9f1c" />
        <path d="M-1 -3.4 Q1 -6.8 4.6 -4.6 Q2 -3 -1 -3.4 Z" fill="#f6d84a" />
        <path d="M-6.4 -0.6 H6.4 L5 5.6 H-5 Z" fill="#b5793f" />
        <path d="M-5.8 1.6 H5.8 M-5.4 3.6 H5.4" stroke="#7a4a24" strokeWidth="0.6" />
        <path d="M-3 -0.6 L-3.4 5.6 M0 -0.6 V5.6 M3 -0.6 L3.4 5.6" stroke="#7a4a24" strokeWidth="0.5" />
      </g>
    ),
  },
};

export const MOTIF_NAMES = Object.fromEntries(Object.entries(MOTIFS).map(([k, v]) => [k, Object.keys(v)]));

/** one motif, or null */
export const motif = (season, name) => (MOTIFS[season] && MOTIFS[season][name]) || SMALL[name] || null;

/** a motif placed at (x,y) at scale s */
export const Stamp = ({ season, name, x = 0, y = 0, s = 1, r = 0 }) => {
  const art = motif(season, name);
  if (!art) return null;
  return <g transform={`translate(${x} ${y}) rotate(${r}) scale(${s})`}>{art}</g>;
};
